import { useState, useEffect, useCallback } from 'react'
import { gql, useLazyQuery } from '@apollo/client'
import { Metadata, ResultsList, WhereGraphql } from 'src/admin/core/models'
import _ from 'lodash'
import { AgencyWeeklySales } from '../../weeklyBalance/models/AgencyWeeklySales'
import { HorseRacesWager } from 'src/core/models'

const GET_WORKSTATION_BALANCES = gql`
  query GetWorkstationTicketTransactions(
    $provider: String!
    $enterpriseId: String!
    $agencyId: String!
    $workstationId: String!
    $currency: CurrencyType!
    $product: ProductEnum!
    $from: DateTime
    $to: DateTime
  ) {
    workstationTicketTransactions(
      provider: $provider
      enterpriseId: $enterpriseId
      agencyId: $agencyId
      workstationId: $workstationId
      currency: $currency
      product: $product
      from: $from
      to: $to
    ) {
      transaction {
        date
        transaction
        amount
        product
        enterpriseId
        agencyId
        workstationId
        transactionEvent {
          transactionType
          surcharge
          number
          providerTickets
        }
      }
      ticket {
        horseRacesWagerCollections {
          providerTicketId
          race
          event {
            league {
              key
              name
            }
          }
          wagers {
            id
    date
    poolId
    combine
    race
    pick
    legs {
      raceId
      runners {
        runnerId
        entries {
          id
          trainer
          jockey
          name
          position
          __typename
        }
        odds
        scratch
        __typename
      }
      __typename
    }
    bet
    modifier
    quick
    takeDown
    displayValue
    wagerCombinations
    total
    commission
          }
        }
      }
    }
    workstationAccountTransactions(
      enterpriseId: $enterpriseId
      agencyId: $agencyId
      workstationId: $workstationId
      currency: $currency
      product: $product
      from: $from
      to: $to
    ) {
      date
      transaction
      amount
      product
      enterpriseId
      agencyId
      workstationId
      transactionEvent {
        transactionType
      }
    }
  }
`

interface Data {
  workstationTicketTransactions: {
    transaction: {
      date: string;
      transaction: string;
      amount: number;
      product: string;
      enterpriseId: string;
      agencyId: string;
      workstationId: string;
      transactionEvent: {
        transactionType: string;
      };
    };
    ticket: {
      horseRacesWagerCollections: {
        providerTicketId: string;
        race: string;
        event: {
          league: {
            key: string;
            name: string;
          };
        };
        wagers: HorseRacesWager[];
      }[];
    };
  }[];
  workstationAccountTransactions: {
    date: string;
    transaction: string;
    amount: number;
    product: string;
    enterpriseId: string;
    agencyId: string;
    workstationId: string;
    transactionEvent: {
      transactionType: string;
      number: string;
      providerTickets: string;
    };
  }[];
}

export const useWorkstationTransactionHistory = () => {
  const [getResults, { loading, error, data }] = useLazyQuery<Data>(GET_WORKSTATION_BALANCES, {
    fetchPolicy: 'network-only',
  });

  const [results, setResults] = useState<ResultsList<AgencyWeeklySales, Metadata>>({
    results: [],
    metadata: {},
  });
  

  const getWorkstationBalances = useCallback(
    ({
      product,
      currency,
      from,
      to,
      where,
      provider,
      enterpriseId,
      workstationId,
    }: {
      product: string
      currency: string
      from: string
      to: string
      where?: WhereGraphql | WhereGraphql[]
      provider?: string
      enterpriseId?: string
      workstationId?: string
    }) => {
      // Derive agencyId from provided where filter when possible
      let agencyId = ''
      if (where && !Array.isArray(where)) {
        const maybeKey: any = (where as any).key
        if (maybeKey?.id?.eq) {
          agencyId = maybeKey.id.eq as string
        }
      }

      getResults({
        variables: {
          product,
          currency,
          to,
          from,
          provider: provider ?? '',
          enterpriseId: enterpriseId ?? '',
          agencyId,
          workstationId: workstationId ?? '',
        },
      })
    },
    [getResults],
  )

  useEffect(() => {
    if (data?.workstationTicketTransactions || data?.workstationAccountTransactions) {
      const ticketTransactions = data.workstationTicketTransactions || []
      const accountTransactions = data.workstationAccountTransactions || []
      
      // Combine both transaction types
      const allTransactions = [
        ...ticketTransactions.map((item: any) => ({
          ...item.transaction,
          ticket: item.ticket,
          type: 'ticket'
        })),
        ...accountTransactions.map((item: any) => ({
          ...item,
          type: 'account'
        }))
      ]

      // Sort by date from oldest to newest
      const sortedTransactions = allTransactions.sort((a, b) => {
        const dateA = new Date(a.date).getTime()
        const dateB = new Date(b.date).getTime()
        return dateA - dateB // Ascending order (oldest first)
      })

      // Calculate running balance
      let runningBalance = 0
      const transactionsWithBalance = sortedTransactions.map((transaction) => {
        const surcharge = transaction.transactionEvent?.surcharge || 0
        const totalAmount = transaction.amount + surcharge
        
        // DEBIT adds to balance, CREDIT subtracts from balance
        if (transaction.transaction === 'DEBIT') {
          runningBalance += totalAmount
        } else if (transaction.transaction === 'CREDIT') {
          runningBalance -= totalAmount
        }
        
        return {
          ...transaction,
          amount: totalAmount,
          balance: runningBalance
        }
      })

      setResults({
        results: transactionsWithBalance as any,
        metadata: {
          Count: transactionsWithBalance.length,
        },
      })
    }
  }, [data])

  return {
    loading,
    error,
    results,
    getWorkstationBalances,
  }
}

